// Projects — shelves the EmCog keeps (Oct 23 2026: "the project should be a shelf. There are no new threads of our
// EmCogs"). The grid of shelves, a new-project form (the EmCog is told), and a shelf: where you left off (the
// EmCog's own note), what's on it (opens in the workbench), next steps, what was decided — and "Work on this",
// which puts the shelf in front of the EmCog in the one conversation.
import React, { useContext, useEffect, useRef, useState } from 'react';
import { WorkbenchContext } from '../../contexts/WorkbenchContext';
import { useLibraryDelete, useWorkspaceCall, useWorkspaceData, when } from './Places';

export interface ShelfView {
  id: string; name: string; why: string; status: 'active' | 'paused' | 'done'; startedBy: 'person' | 'emcog';
  createdAt: number; touchedAt: number; where: { text: string; at: number } | null;
  next: Array<{ id: string; text: string; done: boolean; at: number }>; decided: Array<{ text: string; at: number }>; itemCount: number;
}

const STATUS: Record<string, string> = { active: 'Active', paused: 'Paused', done: 'Done' };

export const NewProject: React.FC<{ emcogName: string; onClose: () => void; onStarted: (id: string, name: string) => void }> = ({ emcogName, onClose, onStarted }) => {
  const call = useWorkspaceCall();
  const [name, setName] = useState('');
  const [why, setWhy] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const first = useRef<HTMLInputElement>(null);
  useEffect(() => { first.current?.focus(); const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); }; window.addEventListener('keydown', esc); return () => window.removeEventListener('keydown', esc); }, [onClose]);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) { setError('Give the project a name.'); return; }
    setBusy(true); setError(null);
    try { const r = await call('/api/workspace/projects', 'POST', { name: name.trim(), why: why.trim() }); onStarted(r.project.id, r.project.name); onClose(); }
    catch (err) { setError(err instanceof Error ? err.message : String(err)); setBusy(false); }
  };
  return (
    <div className="ws-scrim" onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
      <form className="ws-modal" onSubmit={submit} aria-labelledby="ws-np-title">
        <h3 id="ws-np-title">New project</h3>
        <p className="ws-modal-lede">{emcogName} keeps the shelf: what you make together lands on it, and {emcogName} remembers where you left off.</p>
        <label htmlFor="ws-np-name">Name<input id="ws-np-name" ref={first} value={name} onChange={e => setName(e.target.value)} placeholder="Harvest Fair poster" autoComplete="off" /></label>
        <label htmlFor="ws-np-why"><span>What it's for <span className="ws-faint">(optional)</span></span><textarea id="ws-np-why" rows={3} value={why} onChange={e => setWhy(e.target.value)} placeholder={`A sentence or two — ${emcogName} takes it from there.`} /></label>
        {error && <div className="ws-form-error" role="alert">{error}</div>}
        <div className="ws-modal-acts">
          <button type="button" className="ws-btn" onClick={onClose}>Cancel</button>
          <button type="submit" className="ws-btn ws-primary" disabled={busy}>{busy ? 'Starting…' : 'Start project'}</button>
        </div>
      </form>
    </div>
  );
};

export const ProjectsPlace: React.FC<{ emcogName: string; focus: string | null; onOpen: (id: string) => void; onNew: () => void }> = ({ emcogName, focus, onOpen, onNew }) => {
  const { data, error } = useWorkspaceData<{ focus: string | null; projects: ShelfView[] }>('/api/workspace/projects', 30_000);
  const [showDone, setShowDone] = useState(false);
  const all = data?.projects || [];
  const shown = all.filter(p => showDone || p.status !== 'done');
  return (
    <div className="ws-wrap">
      <div className="ws-toolbar">
        <span />
        {all.some(p => p.status === 'done') && <button className="ws-btn" onClick={() => setShowDone(s => !s)}>{showDone ? 'Hide finished' : 'Show finished'}</button>}
      </div>
      {error && <div className="ws-empty">{error}</div>}
      {!error && data && !shown.length && <div className="ws-empty">No projects yet. <button className="ws-linkbtn ws-inline" onClick={onNew}>Start one</button>, or ask {emcogName} to.</div>}
      <div className="ws-grid">
        {shown.map(p => (
          <button key={p.id} className="ws-card ws-proj" onClick={() => onOpen(p.id)}>
            <div className="ws-proj-top">
              <span className={`ws-pill ${p.status === 'active' ? 'ws-pill-live' : ''}`}>{STATUS[p.status]}</span>
              {focus === p.id && <span className="ws-pill ws-pill-on">Working on it</span>}
            </div>
            <h3>{p.name}</h3>
            <div className="ws-proj-where">{p.where ? p.where.text : p.why || 'Just started.'}</div>
            <div className="ws-card-meta"><span>{p.itemCount} on the shelf · {p.next.filter(n => !n.done).length} next</span><span>{when(p.touchedAt)}</span></div>
          </button>
        ))}
      </div>
    </div>
  );
};

export const ProjectView: React.FC<{ id: string; emcogName: string; focus: string | null; onWork: (id: string) => void; onBack: () => void }> = ({ id, emcogName, focus, onWork, onBack }) => {
  const bench = useContext(WorkbenchContext);
  const call = useWorkspaceCall();
  const removePiece = useLibraryDelete();
  const { data, error, reload } = useWorkspaceData<{ project: ShelfView; items: any[]; focused: boolean }>(`/api/workspace/projects/${encodeURIComponent(id)}`, 30_000);
  const [step, setStep] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const run = async (path: string, method = 'POST', body?: any) => { try { await call(path, method, body); setProblem(null); } catch (e) { setProblem(e instanceof Error ? e.message : String(e)); } reload(); };
  if (error) return <div className="ws-wrap"><div className="ws-empty">{error}</div></div>;
  if (!data) return <div className="ws-wrap"><div className="ws-empty">Opening…</div></div>;
  const p = data.project;
  const open = p.next.filter(n => !n.done), done = p.next.filter(n => n.done).slice(-6).reverse();
  const addStep = async (e: React.FormEvent) => { e.preventDefault(); const t = step.trim(); if (!t) return; setStep(''); await run(`/api/workspace/projects/${p.id}/next`, 'POST', { text: t }); };
  const removeProject = () => bench.confirm({
    title: `Delete the "${p.name}" project?`,
    body: `The shelf goes — where you left off, next steps and what was decided. What's on it stays in the Library. ${emcogName} knows you deleted it.`,
    action: 'Delete the project',
    run: async () => { await call(`/api/workspace/projects/${p.id}`, 'DELETE'); bench.note(`Deleted "${p.name}"`); onBack(); }
  });
  return (
    <div className="ws-wrap">
      <button className="ws-linkbtn" onClick={onBack}>← All projects</button>
      <div className="ws-proj-head">
        <div className="ws-proj-head-tt">
          <h1>{p.name}</h1>
          {p.why && <p>{p.why}</p>}
        </div>
        <div className="ws-proj-acts">
          <label className="ws-sr" htmlFor="ws-proj-status">Status</label>
          <select id="ws-proj-status" className="ws-select" value={p.status} onChange={e => run(`/api/workspace/projects/${p.id}/status`, 'POST', { status: e.target.value })}>
            <option value="active">Active</option><option value="paused">Paused</option><option value="done">Done</option>
          </select>
          {focus === p.id
            ? <button className="ws-btn" onClick={() => run(`/api/workspace/projects/${p.id}/focus`, 'POST', { on: false })}>Stop working on it</button>
            : <button className="ws-btn ws-primary" onClick={() => onWork(p.id)}>Work on this with {emcogName}</button>}
          <button className="ws-btn ws-danger-quiet" onClick={removeProject}>Delete</button>
        </div>
      </div>
      {problem && <div className="ws-form-error" role="alert">{problem}</div>}
      <div className="ws-where">
        <div className="ws-where-by"><span className="ws-dot-alive" />Where you left off · {p.where ? `${emcogName}'s note, ${when(p.where.at)}` : `${emcogName} writes this as you work`}</div>
        <p>{p.where ? p.where.text : 'Nothing yet. Work on it together and the note fills in.'}</p>
      </div>
      <div className="ws-cols">
        <section>
          <h2 className="ws-sec-h">On the shelf <span>{data.items.length}</span></h2>
          <div className="ws-list">
            {data.items.length ? data.items.map((i: any) => (
              <div key={i.id} className="ws-row-wrap">
                <button className="ws-row" onClick={() => bench.open?.({ title: i.title, view: i.view, libraryId: i.id, ...(i.pageUrl ? { url: i.pageUrl } : {}), fileName: i.fileName, meta: when(i.at) })}>
                  <span className={`ws-k ws-k-${i.view}`}>{String(i.kind).slice(0, 4).toUpperCase()}</span>
                  <span className="ws-row-tt"><b>{i.title}</b><span>{when(i.at)}</span></span>
                </button>
                <button className="ws-todo-x" aria-label={`Delete "${i.title}"`} title="Delete" onClick={() => removePiece(i)}>✕</button>
              </div>
            )) : <div className="ws-row ws-row-empty">Nothing here yet — what {emcogName} makes while you work on this lands here.</div>}
          </div>
        </section>
        <section>
          <h2 className="ws-sec-h">Next</h2>
          <div className="ws-list">
            {open.map(n => (
              <div className="ws-todo ws-row" key={n.id}>
                <button className="ws-check" role="checkbox" aria-checked={false} aria-label={`Mark "${n.text}" done`} onClick={() => run(`/api/workspace/projects/${p.id}/next/${n.id}`, 'POST', { done: true })} />
                <span className="ws-todo-tt">{n.text}</span>
                <button className="ws-todo-x" aria-label={`Remove "${n.text}"`} onClick={() => run(`/api/workspace/projects/${p.id}/next/${n.id}`, 'DELETE')}>✕</button>
              </div>
            ))}
            <form className="ws-row ws-todo-add" onSubmit={addStep}>
              <label className="ws-sr" htmlFor="ws-proj-step">Add a next step</label>
              <input id="ws-proj-step" value={step} onChange={e => setStep(e.target.value)} placeholder="Add a next step…" autoComplete="off" />
            </form>
            {done.map(n => (
              <div className="ws-todo ws-row done" key={n.id}>
                <button className="ws-check" role="checkbox" aria-checked={true} aria-label={`Put "${n.text}" back`} onClick={() => run(`/api/workspace/projects/${p.id}/next/${n.id}`, 'POST', { done: false })} />
                <span className="ws-todo-tt">{n.text}</span>
                <span />
              </div>
            ))}
          </div>
          {p.decided.length > 0 && (
            <>
              <h2 className="ws-sec-h">Decided</h2>
              <div className="ws-list">{p.decided.slice().reverse().map((d, i) => <div className="ws-row" key={i}><span className="ws-row-tt">{d.text}</span></div>)}</div>
            </>
          )}
        </section>
      </div>
    </div>
  );
};
